// src/lib/reach-spalten.ts
//
// Modul 38 / A7 — der Spaltenplan der Reichweiten-Tabelle.
//
// Warum eine Tabelle aus Zahlen und keine Tailwind-Klassen an den Spalten?
// Die Zusage aus dem Plan lautet: „Bei 1600 px Breite kein horizontaler Scrollbalken".
// Genau das laesst sich in einem Test NICHT pruefen — jsdom rechnet kein Layout, jede
// Breite dort ist 0. Pruefbar ist nur die Rechnung DAHINTER: passt die Summe der festen
// Spaltenbreiten plus das Mindestmass der dehnbaren Spalte in den Platz, den die Tabelle
// bei 1600 px Fensterbreite wirklich hat? Diese Datei macht diese Rechnung zum Gegenstand
// des Codes, damit eine spaetere Spalte sie roetet, statt sie still zu sprengen.
//
// Der Platz bei 1600 px Fensterbreite. Jeder Abzug steht einzeln in
// `BREITEN_HERLEITUNG` — nicht als Kommentar, sondern als Zahl, die `Z_A7n` gegen die
// CSS-Quellen prueft. Ein Kommentar haette die Rechnung erklaert, ohne sie zu binden:
// waechst die Seitenleiste in `index.css`, bliebe eine reine Konstante still falsch
// (Codex-Review 2026-08-18, Befund 8, erster Punkt).
export const BREITEN_HERLEITUNG = {
  /** Fensterbreite, fuer die die Zusage aus dem Plan gilt. */
  fenster: 1600,
  /** Seitenleiste — `--sidebar-w` in src/index.css. */
  seitenleiste: 236,
  /** Seitenrand der Seite — `p-8` links + rechts in InventoryReach. */
  seitenrand: 64,
  /** Rahmen des Panels, 1 px je Seite. Innenrand entfaellt: `bodyClassName="p-0"`. */
  panelRahmen: 2,
  /**
   * Senkrechte Rollbalken, die von der Fensterbreite abgehen. Gerechnet werden ZWEI:
   *  1. der Seiten-Rollbalken — die Seite ist mit Kopf, Filtern und Fusszeile laenger
   *     als das Fenster;
   *  2. Reserve. Frueher war das der eigene Scrollport der Tabelle (`max-h-[70vh]`); seit
   *     2026-10-26 rollt nur noch die Seite (DataTable `stickyHeader`). Die Reserve bleibt,
   *     damit die Zusage auch mit breiteren Rollbalken oder Zoom-Rundung haelt.
   * In der ersten Fassung fehlten Rollbalken ganz (Gemini-Befund 1 / Codex 4).
   */
  rollbalkenAnzahl: 2,
  /**
   * Breite eines Rollbalkens. 17 px ist der klassische Windows-Wert und damit die
   * ungeguenstigste verbreitete Annahme; ueberlagernde Rollbalken (macOS) brauchen 0 px,
   * dort bleibt der Platz ungenutzt. Bewusst nach oben gerundet: zu viel Reserve kostet
   * Namensbreite, zu wenig kostet die Zusage.
   */
  rollbalkenBreite: 17,
} as const

/** Nutzbare Breite der Tabelle bei 1600 px — Ergebnis von `BREITEN_HERLEITUNG`. */
export const NUTZBARE_TABELLENBREITE_1600 =
  BREITEN_HERLEITUNG.fenster -
  BREITEN_HERLEITUNG.seitenleiste -
  BREITEN_HERLEITUNG.seitenrand -
  BREITEN_HERLEITUNG.panelRahmen -
  BREITEN_HERLEITUNG.rollbalkenAnzahl * BREITEN_HERLEITUNG.rollbalkenBreite

/** Mindestbreite der dehnbaren Namensspalte. Darunter ist der Name auch gekuerzt
 *  wertlos; der Volltext haengt ohnehin im `title`. */
export const REACH_NAME_MIN_BREITE = 160

/** Laengste zulaessige Kopfzeile. Die Grenze ist der eigentliche Grund fuer `kurz`/`lang`:
 *  „Offene Bestellungen" als Kopfzeile zwang die Spalte auf ihre Textbreite und schob die
 *  Tabelle aus dem Bild. Wer eine Kopfzeile verlaengert, roetet Z_A7f. */
export const KOPFZEILE_MAX_ZEICHEN = 12

export type ReachSpalte =
  | "select"
  | "dr_stock_id"
  | "merchant_sku"
  | "name"
  | "supplier"
  | "sold_window"
  | "avg_month"
  | "quantity"
  | "reach_months"
  | "stock_value_ek"
  | "open_notes"
  | "quick_order"
  | "notes"

export interface ReachSpaltenPlatz {
  key: ReachSpalte
  /** Kopfzeile in der Tabelle. Leer = die Spalte traegt keine Beschriftung. */
  kurz: string
  /** Volle Bedeutung. Steckt im `title` der Kopfzelle, damit das Kuerzen nichts kostet. */
  lang: string
  /** Feste Breite in px. `null` = diese Spalte bekommt den Rest (genau eine). */
  breite: number | null
  /** Darf der Nutzer sie ueber das Spalten-Menue ausblenden? */
  ausblendbar: boolean
}

// Die Reihenfolge dieser Liste IST die Reihenfolge der Spalten. `quick_order`
// („Bestellen") steht ZULETZT — der Plan verlangt sie woertlich „immer sichtbar rechts",
// und in der ersten Fassung stand die Notiz-Spalte noch rechts daneben
// (Codex-Review 2026-08-18, Befund 2). `Z_A7o` haelt das fest.
//
// Die Breiten sind nicht geraten, sondern am breitesten realen Inhalt bemessen (je
// zuzueglich `p-2` = 16 px Zellrand):
//   reach_months 144  Icon 16 + Abstand 8 + Pille „Bald nachbestellen" — die bricht in
//                     zwei Zeilen um (`whitespace-normal` an der Spalte); 144 traegt das
//                     laengste EINZELWORT („nachbestellen"), das nicht brechen kann.
//   notes         44  Notiz-Knopf `size-7` = 28 px.
//   quick_order   88  Zahlenfeld `w-16` = 64 px.
//   open_notes   144  mehrzeilig, umbrechend.
//   supplier     120  Name wird gekuerzt (`truncate`), Stift-Knopf bleibt stehen.
//   Zahlenspalten 80  „Verkauft"/„Ø/Monat"/„Bestand" als Kopfzeile = 8 Zeichen.
//   stock_value_ek 96 „40.000,00 €" in `text-xs`.
//
// Was passiert, wenn ein Inhalt DOCH breiter ist als seine Spalte? Er wird gekuerzt und
// haengt vollstaendig im `title` — nie laeuft er in die Nachbarzelle. `table-fixed` allein
// leistet das nicht; es verteilt nur die Breiten, es kappt nichts (Codex-Befund 1).
export const REACH_SPALTEN: readonly ReachSpaltenPlatz[] = [
  { key: "select", kurz: "", lang: "Zeile auswählen", breite: 36, ausblendbar: false },
  { key: "dr_stock_id", kurz: "Lagernr.", lang: "Lagernummer", breite: 84, ausblendbar: false },
  { key: "merchant_sku", kurz: "Sternnr.", lang: "Sternnummer (Händlerartikelnummer)", breite: 100, ausblendbar: false },
  { key: "name", kurz: "Name", lang: "Artikelname", breite: null, ausblendbar: false },
  { key: "supplier", kurz: "Lieferant", lang: "Lieferant", breite: 120, ausblendbar: true },
  { key: "sold_window", kurz: "Verkauft", lang: "Verkauft im gewählten Zeitfenster", breite: 80, ausblendbar: true },
  { key: "avg_month", kurz: "Ø/Monat", lang: "Durchschnittlicher Absatz je Monat", breite: 80, ausblendbar: true },
  { key: "quantity", kurz: "Bestand", lang: "Bestand laut Lager", breite: 80, ausblendbar: false },
  { key: "reach_months", kurz: "Reichweite", lang: "Reichweite in Monaten", breite: 144, ausblendbar: false },
  { key: "stock_value_ek", kurz: "Lagerwert", lang: "Lagerwert zum Einkaufspreis", breite: 96, ausblendbar: true },
  // „Bestellt" ist NICHT ausblendbar, obwohl der Plan sie dafuer vorsah: sie ist die
  // einzige Spalte, die zeigt, dass zu diesem Artikel schon etwas unterwegs ist. Wer sie
  // ausblendet und bestellt, bestellt bei ~6 Monaten Nachlieferzeit doppelt — und der
  // Merkposten haette diesen Zustand ueber Sitzungen hinweg festgehalten. Mit festen
  // Breiten passt die Tabelle ohnehin; die Ausblendbarkeit haette hier nichts gekauft
  // und Geld gekostet. (Gemini-Befund 3 / Codex-Befund 3; Abweichung vom Plan, an den
  // Koordinator gemeldet.)
  { key: "open_notes", kurz: "Bestellt", lang: "Offene Bestellungen und Vormerkungen", breite: 144, ausblendbar: false },
  { key: "notes", kurz: "", lang: "Artikel-Notizen", breite: 44, ausblendbar: false },
  { key: "quick_order", kurz: "Bestellen", lang: "Bestellmenge für die Sammel-Erfassung", breite: 88, ausblendbar: false },
]

export const SPEICHER_SCHLUESSEL = "stahlia.reach.versteckte-spalten"

const NACH_SCHLUESSEL = new Map<string, ReachSpaltenPlatz>(REACH_SPALTEN.map((s) => [s.key, s]))

/** Sichtbare Spalten in Anzeige-Reihenfolge. Eine Spalte, die nicht `ausblendbar` ist,
 *  bleibt sichtbar — auch wenn sie im Merkposten steht (fremder Speicherstand, alte
 *  Version, Handeintrag). Ohne Auswahl-Haken und „Bestellen" waere die Seite unbedienbar. */
export function sichtbareSpalten(versteckt: Iterable<string>): ReachSpaltenPlatz[] {
  const aus = new Set(versteckt)
  return REACH_SPALTEN.filter((s) => !(s.ausblendbar && aus.has(s.key)))
}

/** Breiten-Rechnung fuer eine Spaltenauswahl: was steht fest, was bleibt fuer die
 *  dehnbare Spalte uebrig.
 *
 *  `rest` ist die reine Differenz und darf negativ werden. Das ist Absicht: die Regel
 *  „`rest` >= REACH_NAME_MIN_BREITE" wird in `Z_A7a` GEPRUEFT, nicht hier erzwungen.
 *  Ein `Math.max()` an dieser Stelle wuerde ein gesprengtes Budget rechnerisch heilen
 *  und genau die Zusicherung blind machen, fuer die es diese Funktion gibt
 *  (Gemini-Review 2026-08-18, Befund 8 — Diagnose uebernommen, Fix bewusst anders). */
export function breitenBudget(spalten: readonly ReachSpaltenPlatz[]): {
  fest: number
  dehnbar: number
  rest: number
} {
  const fest = spalten.reduce((s, sp) => s + (sp.breite ?? 0), 0)
  const dehnbar = spalten.filter((sp) => sp.breite === null).length
  return { fest, dehnbar, rest: NUTZBARE_TABELLENBREITE_1600 - fest }
}

type LeseSpeicher = Pick<Storage, "getItem">
type SchreibSpeicher = Pick<Storage, "setItem">

/** Merkposten lesen. Alles, was nicht als Liste bekannter, ausblendbarer Schluessel
 *  lesbar ist, wird verworfen — ein kaputter Eintrag darf die Seite nicht kosten. */
export function ladeVersteckteSpalten(speicher: LeseSpeicher | null | undefined): Set<ReachSpalte> {
  if (!speicher) return new Set()
  let roh: string | null
  try {
    roh = speicher.getItem(SPEICHER_SCHLUESSEL)
  } catch {
    return new Set()
  }
  if (!roh) return new Set()
  let gelesen: unknown
  try {
    gelesen = JSON.parse(roh)
  } catch {
    return new Set()
  }
  if (!Array.isArray(gelesen)) return new Set()
  const raus = new Set<ReachSpalte>()
  for (const eintrag of gelesen) {
    if (typeof eintrag !== "string") continue
    const platz = NACH_SCHLUESSEL.get(eintrag)
    if (platz?.ausblendbar) raus.add(platz.key)
  }
  return raus
}

export function speichereVersteckteSpalten(
  speicher: SchreibSpeicher | null | undefined,
  versteckt: Iterable<string>,
): void {
  if (!speicher) return
  const gefiltert = [...new Set(versteckt)].filter((k) => NACH_SCHLUESSEL.get(k)?.ausblendbar)
  try {
    speicher.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(gefiltert))
  } catch {
    // Privater Modus / voller Speicher: die Auswahl gilt dann nur fuer diese Sitzung.
  }
}
